import compact from "lodash/compact";
import filter from "lodash/filter";
import find from "lodash/find";
import last from "lodash/last";
import sortBy from "lodash/sortBy";
import { BookingFormData, BookingFormPropsData } from "PFApp/booking/components/booking_form";
import { getBookingFormDetailsPanelKey } from "PFApp/booking/components/booking_form/booking_form.utils";
import {
  PanelKey,
  SidePanelType,
  useSidePanelsActions,
  useSidePanelsStore
} from "PFApp/stores/side_panels_store";
import { useSidePanelCloseExternal } from "PFApp/stores/side_panels_store";
import { Id } from "PFCore/types";
import { useCallback } from "react";

import { DetailsPanelData, DetailsPanelType } from "../details_panel.types";

export const useDetailsPanelsActions = () => {
  const { openPanel, closePanel } = useSidePanelsActions<DetailsPanelData>();
  const closePanelExternal = useSidePanelCloseExternal();
  const panels = useSidePanelsStore((state) => state.panels);

  const openDetailsPanel = useCallback(
    /* The omitted fields might be managed at a higher level - do not set them when opening a panel */
    (data: Omit<DetailsPanelData, "bookingModuleDetails" | "bookingDetails">, stack = false) => {
      const key: PanelKey = `${SidePanelType.BookingDetails}_${data.type}_${data.id}`;
      const lastOpenPanel = panels[panels.length - 1];
      const hasExistingPanelOfSameType =
        lastOpenPanel?.key.startsWith(SidePanelType.BookingDetails) &&
        (lastOpenPanel.data as DetailsPanelData).type === data.type;

      // Preserve higher-level-managed fields, otherwise reopening an already-open panel wipes them
      const existingPanel = find(panels, ["key", key]);
      const { bookingModuleDetails, bookingDetails } = existingPanel?.data ?? {};

      openPanel(
        key,
        { bookingModuleDetails, bookingDetails, ...data },
        stack,
        false,
        hasExistingPanelOfSameType
      );
    },
    [openPanel, panels]
  );

  const closeDetailsPanel = useCallback(
    (type: DetailsPanelType, id: Id) => closePanel(`${SidePanelType.BookingDetails}_${type}_${id}`),
    [closePanel]
  );
  const closeDetailsPanelExternal = useCallback(
    (type: DetailsPanelType, id: Id) => closePanelExternal(`${SidePanelType.BookingDetails}_${type}_${id}`),
    [closePanelExternal]
  );
  const closeAllDetailsPanels = useCallback(
    () => closePanel((panels) => filter(panels, ({ key }) => key.startsWith(SidePanelType.BookingDetails))),
    [closePanel]
  );
  const closeTopDetailsPanel = useCallback(
    () =>
      closePanel((panels) => {
        const filteredPanels = sortBy(
          filter(panels, ({ key }) => key.startsWith(SidePanelType.BookingDetails)),
          "index"
        );
        return compact([last(filteredPanels)]);
      }),
    [closePanel]
  );

  const openBookingFormSidePanel = useCallback(
    (data: BookingFormData, props?: BookingFormPropsData) => {
      const { mode } = props ?? {};
      if (!mode) {
        return;
      }
      // we keep only 1 BookingForm panel open at a time
      closePanel((panels) => panels.filter(({ data }) => data.type === DetailsPanelType.BookingForm));
      openPanel(
        getBookingFormDetailsPanelKey(mode),
        {
          id: DetailsPanelType.BookingForm,
          type: DetailsPanelType.BookingForm,
          bookingFormDetails: { data, props }
        },
        true,
        false,
        false
      );
    },
    [openPanel, closePanel]
  );

  const closeBookingFormSidePanel = useCallback(
    () => closePanel((panels) => panels.filter(({ data }) => data.type === DetailsPanelType.BookingForm)),
    [closePanel]
  );

  return {
    openDetailsPanel,
    closeDetailsPanel,
    closeAllDetailsPanels,
    closeTopDetailsPanel,
    closeDetailsPanelExternal,
    openBookingFormSidePanel,
    closeBookingFormSidePanel
  };
};
